





























































































































import { AxiosError } from 'axios';
import humanizeDuration from 'humanize-duration';
import { PropType } from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';

import { ELoyaltyFeatureFlag } from '@/store/modules/feature-flags/FeatureFlagNames';

import LimitCoefficientDialog, {
  ILimitCoefficientEmitData
} from '@/components/LimitCoefficientDialog.vue';
import StakingConfirmationDialog from './StakingConfirmationDialog.vue';
import StakingFormDialog from './StakingFormDialog.vue';

import {
  NewStakingProgramData,
  ProgramDialogData,
  StakingProgram
} from '@/api/schema';

import { errorToastMessage } from '@/helpers/errorToastMessage';
import { roundNumber } from '@/helpers/roundNumber';

type OperationType =
  | 'delete'
  | 'update'
  | 'create'
  | 'start-all'
  | 'stop-all'
  | 'set-coefficient';

export default {
  name: 'StakingViewPrograms',
  components: {
    LimitCoefficientDialog,
    StakingConfirmationDialog,
    StakingFormDialog
  },
  props: {
    modal: {
      type: Boolean as PropType<boolean>,
      default: (): boolean => false
    }
  },
  data(): any {
    return {
      isPending: false,
      formDialog: false,
      confirmationDialog: false,
      coefficientDialog: false,
      dialogItem: {},
      dialogOperation: null,
      dialogData: null
    };
  },
  computed: {
    ...mapState('Auth', ['tokenSymbol']),
    ...mapState('TokensInfo', ['tokenPrice']),
    ...mapState('StakingPrograms', ['loading', 'programs']),
    ...mapGetters('Onboarding', ['operatorId']),
    ...mapGetters('FeatureFlags', ['isEnabledFeatureFlag']),
    ...mapState('app', ['isSuperAdmin']),
    disabledTable(): boolean {
      return !this.isEnabledFeatureFlag(ELoyaltyFeatureFlag.HOLD_TO_EARN);
    },
    showControls(): boolean {
      return this.$role.can.update('h2e');
    },
    generalTableHeaders(): { [key: string]: unknown }[] {
      return [
        { text: 'Name', value: 'name' },
        { text: 'Reward, %', value: 'rate' },
        { text: 'Active Users', value: 'users' },
        { text: 'Amount Locked', value: 'tokens' },
        ...(this.isLoyaltyLevelRestrictionEnabled
          ? [
              {
                text: 'Loyalty Level Restriction',
                value: 'loyaltyLevelRestriction',
                width: 200
              }
            ]
          : []),
        ...(this.showControls
          ? [{ text: '', value: 'actions', sortable: false }]
          : [])
      ];
    },
    computedPrograms(): StakingProgram[] {
      return this.programs
        .filter((v: StakingProgram) => v.status === 'ACTIVE')
        .map((v: StakingProgram) => ({
          ...v,
          humanType: this.getHumanType(v)
        }));
    },
    isLoyaltyLevelRestrictionEnabled(): boolean {
      return this.isEnabledFeatureFlag(
        ELoyaltyFeatureFlag.HOLD_TO_EARN_LOYALTY_LEVEL_RESTRICTION
      );
    }
  },
  watch: {
    operatorId: {
      handler(newId: number | null): void {
        if (newId) {
          this.getStakingPrograms();
          this.getTokenPriceData();
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions('StakingPrograms', [
      'getStakingPrograms',
      'updateStakingProgram',
      'deleteStakingProgram',
      'stopAllStakingProgram',
      'startAllStakingProgram',
      'setDefaultCoefficient'
    ]),
    ...mapActions('TokensInfo', ['getTokenPriceData']),
    humanizeDuration,
    openDialog(item: StakingProgram, method: OperationType): void {
      this.dialogOperation = method;
      this.dialogData = this.getDialogData(this.dialogOperation);
      this.dialogItem = item;
      const dialogType = this.getDialogTypeByOperation(method);
      this[dialogType] = true;
    },
    closeDialog(): void {
      this.dialogItem = {};
      this.dialogOperation = null;
      this.confirmationDialog = false;
      this.formDialog = false;
      this.coefficientDialog = false;
    },
    getDialogTypeByOperation(operation: string): string {
      return operation === 'create' || operation === 'update'
        ? 'formDialog'
        : operation === 'set-coefficient'
        ? 'coefficientDialog'
        : 'confirmationDialog';
    },
    confirmationDialogHandler(): void {
      const { handler } = this.dialogData;
      handler(this.dialogItem.id)
        .then((): void => {
          this.closeDialog();
        })
        .catch((error: AxiosError): void => {
          console.error(error);
          this.closeDialog();
          errorToastMessage(error);
        });
    },
    coefficientDialogHandler(coefficient: ILimitCoefficientEmitData): void {
      const { handler } = this.dialogData;

      handler(coefficient.value)
        .then((): void => {
          this.closeDialog();
        })
        .catch((error: AxiosError): void => {
          console.error(error);
          this.closeDialog();
          errorToastMessage(error);
        });
    },
    getDialogData(operation: OperationType): ProgramDialogData {
      if (!operation) return;
      switch (operation) {
        case 'create':
          return {
            header: 'Create Program',
            buttonText: 'Create'
          };
        case 'update':
          return {
            header: 'Edit Program',
            buttonText: 'Update'
          };
        case 'delete':
          return {
            handler: this.deleteStakingProgram,
            header: 'Are you sure you want to delete program?'
          };
        case 'start-all':
          return {
            handler: this.startAllStakingProgram,
            header: 'Are you sure you want to start all programs?'
          };
        case 'stop-all':
          return {
            handler: this.stopAllStakingProgram,
            header: 'Are you sure you want to stop all programs?'
          };
        case 'set-coefficient':
          return {
            handler: this.setDefaultCoefficient,
            header: 'Update default limited program coefficient',
            disclaimer:
              'Set new default coefficient for limited programs. This coefficient will be applied to all users except whose, who have exclusive limited coefficient'
          };
      }
    },
    roundNumber,
    getHumanType(item: StakingProgram): string {
      const type = item.type;

      if (item.limited) {
        return 'Limited';
      }

      if (item.type !== 'WELCOME') {
        return '';
      }

      return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
    },
    async handleOnToggleLoyaltyLevelRestrictionStatus(
      item: StakingProgram,
      status: boolean
    ): Promise<void> {
      try {
        if (this.isPending) {
          return;
        }
        this.isPending = true;

        await this.updateStakingProgram({
          id: item.id,
          data: {
            loyaltyLevelRestriction: status
          } as NewStakingProgramData
        });
        await this.getStakingPrograms();
      } catch (error) {
        console.error('handleOnToggleLoyaltyLevelRestrictionStatus: ', error);
        errorToastMessage(error);
      } finally {
        this.isPending = false;
      }
    }
  }
};
